import React, { useRef, useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CardRenderer from './CardRenderer';
import AnimatedText from './AnimatedText';
import nikkiAvatar from '../../assets/nikki/nikki-sm.png';

/**
 * ChatThread — renders messages top→bottom. Each message is a row:
 *   - FORMA (left, primary accent) or USER (right, neutral)
 *   - Optional text bubble, cards stacked below, chip rail at the end.
 * Auto-scrolls to bottom on new messages.
 */
const ChatBubble = ({ msg, onAction, animatedRef, mountTime }) => {
  const navigate = useNavigate();
  const isUser = msg.role === 'user';
  // Messages whose created_at is BEFORE the thread mounted are loaded history
  // and shouldn't replay the typing animation. New messages after mount animate once.
  let isHistorical = false;
  try {
    if (msg.created_at) {
      isHistorical = new Date(msg.created_at).getTime() < mountTime;
    }
  } catch { /* ignore */ }
  const alreadyAnimated = isHistorical || animatedRef?.current?.has(msg.id);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 gap-2.5`} data-testid={`msg-${msg.role}`}>
      {!isUser && (
        <img
          src={nikkiAvatar}
          alt="Nikki"
          title="Nikki"
          className="flex-shrink-0 w-8 h-8 rounded-full border border-primary/40 object-cover select-none"
          data-testid="nikki-avatar"
        />
      )}
      <div className={`max-w-[88%] md:max-w-[78%] space-y-2 ${isUser ? 'items-end' : 'items-start'}`}>
        {!isUser && msg.text && (
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Nikki</p>
        )}
        {msg.text && (
          <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'bg-primary/15 text-white border border-primary/30'
              : 'bg-zinc-900/50 backdrop-blur-md border border-white/10 text-zinc-100'
          }`}>
            {isUser ? msg.text : (
              <AnimatedText
                text={msg.text}
                alreadyAnimated={!!alreadyAnimated}
                onDone={() => { animatedRef?.current?.add(msg.id); }}
              />
            )}
          </div>
        )}
        {(msg.cards || []).map((card, idx) => (
          <CardRenderer key={`${msg.id}-card-${idx}`} card={card} onAction={onAction} />
        ))}
        {(msg.chips || []).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {msg.chips.map((chip, idx) => {
              const Icon = chip.icon ? LucideIcons[chip.icon] : null;
              const handleClick = () => {
                if (chip.action === 'navigate') {
                  navigate(chip.payload?.route || '/dashboard');
                  return;
                }
                onAction?.({ type: chip.action || 'send', payload: chip.payload || {}, label: chip.label });
              };
              const style = chip.style === 'primary'
                ? 'bg-primary text-black hover:bg-primary/90'
                : 'bg-zinc-900/60 border border-zinc-700 text-zinc-300 hover:border-primary hover:text-primary';
              return (
                <button key={`${msg.id}-chip-${idx}`} onClick={handleClick}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-secondary font-bold tracking-wide transition-all ${style}`}
                  data-testid={`chip-${(chip.payload?.intent || chip.label).toString().toLowerCase().replace(/\s+/g,'-')}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {chip.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatThread = ({ messages, onAction, onLoadEarlier, hasEarlier, loadingEarlier, sending }) => {
  const endRef = useRef(null);
  const animatedRef = useRef(new Set());
  const mountTimeRef = useRef(Date.now());
  const lastIdRef = useRef(null);
  // Scroll to bottom ONLY when a new last message arrives (an append) — not when
  // older messages are prepended via "Load earlier", and not on every keystroke
  // of the typing animation. Instant scroll avoids smooth-scroll jank on long threads.
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (lastId && lastId !== lastIdRef.current) {
      lastIdRef.current = lastId;
      endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages]);
  // Keep the typing indicator in view while Nikki is composing a reply.
  useEffect(() => {
    if (sending) endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [sending]);
  return (
    <div className="flex-1 overflow-y-auto px-4 md:px-8 pt-6 pb-4" data-testid="chat-thread">
      <div className="max-w-3xl mx-auto">
        {hasEarlier && onLoadEarlier && (
          <div className="flex justify-center mb-4">
            <button
              onClick={onLoadEarlier}
              disabled={loadingEarlier}
              className="text-xs px-4 py-1.5 rounded-full border border-zinc-700 text-zinc-400 hover:border-primary hover:text-primary transition-colors disabled:opacity-60"
              data-testid="load-earlier-btn"
            >
              {loadingEarlier ? 'Loading…' : 'Load earlier messages'}
            </button>
          </div>
        )}
        {messages.map(m => (
          <ChatBubble key={m.id} msg={m} onAction={onAction} animatedRef={animatedRef} mountTime={mountTimeRef.current} />
        ))}
        {sending && (
          <div className="flex justify-start mb-4 gap-2.5" data-testid="nikki-typing-indicator">
            <img
              src={nikkiAvatar}
              alt="Nikki"
              className="flex-shrink-0 w-8 h-8 rounded-full border border-primary/40 object-cover select-none"
            />
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Nikki</p>
              <div className="rounded-2xl px-4 py-3 bg-zinc-900/50 backdrop-blur-md border border-white/10 inline-flex items-center gap-2">
                <span className="text-xs text-zinc-400">Nikki is typing</span>
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
};

export default ChatThread;
